/**
 * hexagram.js — 卦体：八宫、纳甲、六亲、世应、飞伏
 *
 * 卦以「自下而上」的六个爻表示：lines[0] 是初爻，lines[5] 是上爻。
 * 每爻取值 6/7/8/9（老阴/少阳/少阴/老阳），6 与 9 为动爻。
 *
 * 八宫卦序不是硬编码的表，而是按京房的生成规则算出来的：
 *   本宫 → 初爻变(一世) → 二爻变(二世) → 三爻变(三世)
 *        → 四爻变(四世) → 五爻变(五世)
 *        → 四爻变回(游魂) → 下卦复原为本宫下卦(归魂)
 * 这样任何一卦的宫、世、应都是推导出来的，不会抄错。
 */

import { BRANCH_ELEMENT } from './ganzhi.js';

/* --------------------------------------------------------------- 八卦 */

/** 三爻二进制值（自下而上，阳=1）→ 卦名 */
export const TRIGRAM_BY_BITS = {
  7: '乾', 3: '兑', 5: '离', 1: '震',
  6: '巽', 2: '坎', 4: '艮', 0: '坤'
};

export const TRIGRAM_BITS = Object.fromEntries(
  Object.entries(TRIGRAM_BY_BITS).map(([bits, name]) => [name, Number(bits)])
);

export const TRIGRAM_SYMBOL = {
  乾: '☰', 兑: '☱', 离: '☲', 震: '☳', 巽: '☴', 坎: '☵', 艮: '☶', 坤: '☷'
};

/** 卦名用的自然象 */
export const TRIGRAM_NATURE = {
  乾: '天', 兑: '泽', 离: '火', 震: '雷', 巽: '风', 坎: '水', 艮: '山', 坤: '地'
};

/** 八宫五行 */
export const PALACE_ELEMENT = {
  乾: '金', 兑: '金', 离: '火', 震: '木',
  巽: '木', 坎: '水', 艮: '土', 坤: '土'
};

/* ------------------------------------------------------------- 纳甲 */

/**
 * 纳甲表：每宫六个位置的地支（0–2 为该卦作内卦时，3–5 为作外卦时），
 * 以及内卦天干 / 外卦天干（只有乾坤内外不同干）。
 */
export const NAJIA = {
  乾: { stems: ['甲', '壬'], branches: ['子', '寅', '辰', '午', '申', '戌'] },
  坤: { stems: ['乙', '癸'], branches: ['未', '巳', '卯', '丑', '亥', '酉'] },
  震: { stems: ['庚', '庚'], branches: ['子', '寅', '辰', '午', '申', '戌'] },
  巽: { stems: ['辛', '辛'], branches: ['丑', '亥', '酉', '未', '巳', '卯'] },
  坎: { stems: ['戊', '戊'], branches: ['寅', '辰', '午', '申', '戌', '子'] },
  离: { stems: ['己', '己'], branches: ['卯', '丑', '亥', '酉', '未', '巳'] },
  艮: { stems: ['丙', '丙'], branches: ['辰', '午', '申', '戌', '子', '寅'] },
  兑: { stems: ['丁', '丁'], branches: ['巳', '卯', '丑', '亥', '酉', '未'] }
};

/* ------------------------------------------------------------- 卦名 */

/** [上卦][下卦] → { name, order }，order 为周易通行本卦序 */
export const HEXAGRAM_NAMES = {
  乾: { 乾: ['乾为天', 1], 兑: ['天泽履', 10], 离: ['天火同人', 13], 震: ['天雷无妄', 25], 巽: ['天风姤', 44], 坎: ['天水讼', 6], 艮: ['天山遁', 33], 坤: ['天地否', 12] },
  兑: { 乾: ['泽天夬', 43], 兑: ['兑为泽', 58], 离: ['泽火革', 49], 震: ['泽雷随', 17], 巽: ['泽风大过', 28], 坎: ['泽水困', 47], 艮: ['泽山咸', 31], 坤: ['泽地萃', 45] },
  离: { 乾: ['火天大有', 14], 兑: ['火泽睽', 38], 离: ['离为火', 30], 震: ['火雷噬嗑', 21], 巽: ['火风鼎', 50], 坎: ['火水未济', 64], 艮: ['火山旅', 56], 坤: ['火地晋', 35] },
  震: { 乾: ['雷天大壮', 34], 兑: ['雷泽归妹', 54], 离: ['雷火丰', 55], 震: ['震为雷', 51], 巽: ['雷风恒', 32], 坎: ['雷水解', 40], 艮: ['雷山小过', 62], 坤: ['雷地豫', 16] },
  巽: { 乾: ['风天小畜', 9], 兑: ['风泽中孚', 61], 离: ['风火家人', 37], 震: ['风雷益', 42], 巽: ['巽为风', 57], 坎: ['风水涣', 59], 艮: ['风山渐', 53], 坤: ['风地观', 20] },
  坎: { 乾: ['水天需', 5], 兑: ['水泽节', 60], 离: ['水火既济', 63], 震: ['水雷屯', 3], 巽: ['水风井', 48], 坎: ['坎为水', 29], 艮: ['水山蹇', 39], 坤: ['水地比', 8] },
  艮: { 乾: ['山天大畜', 26], 兑: ['山泽损', 41], 离: ['山火贲', 22], 震: ['山雷颐', 27], 巽: ['山风蛊', 18], 坎: ['山水蒙', 4], 艮: ['艮为山', 52], 坤: ['山地剥', 23] },
  坤: { 乾: ['地天泰', 11], 兑: ['地泽临', 19], 离: ['地火明夷', 36], 震: ['地雷复', 24], 巽: ['地风升', 46], 坎: ['地水师', 7], 艮: ['地山谦', 15], 坤: ['坤为地', 2] }
};

/* --------------------------------------------------------- 八宫卦序 */

const PALACES = ['乾', '兑', '离', '震', '巽', '坎', '艮', '坤'];

/** 世位（1–6）按八宫中的次序，应位 = 世位 ± 3 */
const SHI_BY_RANK = [6, 1, 2, 3, 4, 5, 4, 3];
const RANK_LABEL = ['本宫', '一世', '二世', '三世', '四世', '五世', '游魂', '归魂'];

function bitsToLines(bits) {
  return [bits & 1, (bits >> 1) & 1, (bits >> 2) & 1];
}
function linesToBits(l) {
  return l[0] + l[1] * 2 + l[2] * 4;
}

/** 生成 64 卦的宫位索引：key = 六爻阴阳串(自下而上, '1'/'0') */
function buildPalaceIndex() {
  const index = {};
  for (const palace of PALACES) {
    const base = bitsToLines(TRIGRAM_BITS[palace]);
    const seq = [];
    let lines = [...base, ...base]; // 本宫
    seq.push([...lines]);
    for (let i = 0; i < 5; i += 1) {
      lines = [...lines];
      lines[i] = lines[i] ^ 1;
      seq.push([...lines]);
    }
    // 游魂：五世卦的四爻变回
    const soul = [...seq[5]];
    soul[3] = soul[3] ^ 1;
    seq.push(soul);
    // 归魂：游魂卦的下卦复原为本宫下卦
    const ret = [...soul.slice(0, 0), ...base, ...soul.slice(3)];
    seq.push(ret);

    seq.forEach((l, rank) => {
      index[l.join('')] = {
        palace,
        element: PALACE_ELEMENT[palace],
        rank,
        rankLabel: RANK_LABEL[rank],
        shi: SHI_BY_RANK[rank],
        ying: SHI_BY_RANK[rank] > 3 ? SHI_BY_RANK[rank] - 3 : SHI_BY_RANK[rank] + 3
      };
    });
  }
  return index;
}

export const PALACE_INDEX = buildPalaceIndex();

/* ------------------------------------------------------------ 五行 */

const SHENG = { 木: '火', 火: '土', 土: '金', 金: '水', 水: '木' };
const KE = { 木: '土', 土: '水', 水: '火', 火: '金', 金: '木' };

export const generates = (a, b) => SHENG[a] === b;
export const overcomes = (a, b) => KE[a] === b;

/** 以 self 五行为「我」，判断 other 的六亲 */
export function sixRelative(selfEl, otherEl) {
  if (selfEl === otherEl) return '兄弟';
  if (SHENG[otherEl] === selfEl) return '父母'; // 生我者
  if (SHENG[selfEl] === otherEl) return '子孙'; // 我生者
  if (KE[otherEl] === selfEl) return '官鬼'; // 克我者
  if (KE[selfEl] === otherEl) return '妻财'; // 我克者
  throw new Error(`五行关系异常：${selfEl} / ${otherEl}`);
}

/* --------------------------------------------------------- 六神 */

const SIX_GODS = ['青龙', '朱雀', '勾陈', '螣蛇', '白虎', '玄武'];

/** 由日干起六神，自初爻向上排 */
export function sixGods(dayStem) {
  const start = { 甲: 0, 乙: 0, 丙: 1, 丁: 1, 戊: 2, 己: 3, 庚: 4, 辛: 4, 壬: 5, 癸: 5 }[dayStem];
  if (start === undefined) throw new Error(`未知日干：${dayStem}`);
  return Array.from({ length: 6 }, (_, i) => SIX_GODS[(start + i) % 6]);
}

/* -------------------------------------------------------- 装卦 */

/**
 * 由六爻阴阳（自下而上，1 阳 0 阴）装出完整卦体。
 * 返回卦名、宫、世应、每爻的干支/五行/六亲。
 */
export function buildHexagram(lines) {
  if (!Array.isArray(lines) || lines.length !== 6) throw new Error('需要六个爻');
  const lower = TRIGRAM_BY_BITS[linesToBits(lines.slice(0, 3))];
  const upper = TRIGRAM_BY_BITS[linesToBits(lines.slice(3, 6))];
  const [name, order] = HEXAGRAM_NAMES[upper][lower];
  const info = PALACE_INDEX[lines.join('')];

  const yao = lines.map((yin, i) => {
    const trigram = i < 3 ? lower : upper;
    const na = NAJIA[trigram];
    const branch = na.branches[i < 3 ? i : i - 3 + 3];
    const stem = na.stems[i < 3 ? 0 : 1];
    const element = BRANCH_ELEMENT[branch];
    return {
      position: i + 1,
      yin: yin === 0,
      yang: yin === 1,
      stem,
      branch,
      ganzhi: stem + branch,
      element,
      relative: sixRelative(info.element, element),
      isShi: i + 1 === info.shi,
      isYing: i + 1 === info.ying
    };
  });

  return {
    lines: [...lines],
    key: lines.join(''),
    name,
    order,
    upper,
    lower,
    symbol: TRIGRAM_SYMBOL[upper] + TRIGRAM_SYMBOL[lower],
    composition: `上${upper}${TRIGRAM_NATURE[upper]} · 下${lower}${TRIGRAM_NATURE[lower]}`,
    palace: info.palace,
    palaceElement: info.element,
    rankLabel: info.rankLabel,
    shi: info.shi,
    ying: info.ying,
    yao
  };
}

/**
 * 飞伏：本卦六亲不全时，从本宫首卦（八纯卦）取缺失六亲作伏神，
 * 伏于本宫卦中该六亲所在的爻位之下，该位本卦之爻即为飞神。
 */
export function hiddenSpirits(hex) {
  const pure = buildHexagram([
    ...bitsToLines(TRIGRAM_BITS[hex.palace]),
    ...bitsToLines(TRIGRAM_BITS[hex.palace])
  ]);
  const present = new Set(hex.yao.map((y) => y.relative));
  const hidden = [];
  pure.yao.forEach((y, i) => {
    if (!present.has(y.relative)) {
      hidden.push({
        position: i + 1,
        relative: y.relative,
        ganzhi: y.ganzhi,
        branch: y.branch,
        element: y.element,
        flying: {
          relative: hex.yao[i].relative,
          ganzhi: hex.yao[i].ganzhi,
          element: hex.yao[i].element
        }
      });
    }
  });
  return hidden;
}

export { bitsToLines, linesToBits };
